import React from 'react';
import { CheckCircle, AlertTriangle, XCircle, Type, Hash, CheckSquare, SquareStack, Box, CircleDot, FileText, MapPin, Globe, Flag, Shield, Regex, Plus } from 'lucide-react'; // Added Globe, Flag, Shield, Regex, Plus
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/common/Tooltip';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import clsx from 'clsx';
import { getTypeColorClass } from '../../utils/schemaUtils';
import { Parameter } from '../../types/schema';
import { getTransformConfig, PlaybookTransformConfig } from '@/config/playbookTransforms';
import { SchemaProperty } from '@/utils/playbookSchemaUtils';

interface SchemaParameterTreeViewProps {
  param: Parameter; // Use only Parameter type
  onPropertyClick?: (property: SchemaProperty) => void;
  onCreateTransform?: (transformType: 'mapping' | 'enrichment' | 'expression' | 'spreading', propertyPath: string, isInputSchema: boolean) => void;
  currentSchemaView?: 'input' | 'output' | 'yaml';
  selectedSection?: 'global' | 'event' | 'connection';
  selectedEventId?: string; // Add selectedEventId prop
}

const SchemaParameterTreeView: React.FC<SchemaParameterTreeViewProps> = ({
  param,
  onPropertyClick,
  onCreateTransform,
  currentSchemaView = 'output',
  selectedEventId, // Destructure selectedEventId
}) => {
  // Cast param to SchemaProperty to access status, source, etc.
  const schemaPropertyParam = param as Parameter & SchemaProperty;

  // Determine if we should show the create transform dropdown
  const shouldShowCreateTransformDropdown = () => {
    // Only show dropdown if there's no existing transform for this property
    // and we have the onCreateTransform callback
    return !schemaPropertyParam.sourceTransformId && onCreateTransform && currentSchemaView !== 'yaml';
  };

  // Get available transform types based on schema view
  const getAvailableTransformTypes = () => {
    if (currentSchemaView === 'input') {
      return [
        { type: 'mapping' as const, label: 'Mapping', description: 'Map this input to an output field' },
        { type: 'spreading' as const, label: 'Spreading', description: 'Spread this input across multiple outputs' },
        { type: 'expression' as const, label: 'Expression', description: 'Use this input in a custom expression' },
      ];
    } else {
      return [
        { type: 'mapping' as const, label: 'Mapping', description: 'Map an input to this output field' },
        { type: 'enrichment' as const, label: 'Enrichment', description: 'Enrich this output with generated data' },
        { type: 'expression' as const, label: 'Expression', description: 'Generate this output with a custom expression' },
      ];
    }
  };

  const handleCreateTransform = (transformType: 'mapping' | 'enrichment' | 'expression' | 'spreading') => {
    if (onCreateTransform) {
      const isInputSchema = currentSchemaView === 'input';
      onCreateTransform(transformType, param.fullPath, isInputSchema);
    }
  };

  const getStatusIcon = () => {
    switch (schemaPropertyParam.status) {
      case 'mapped':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'missing':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'incomplete':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      case 'extra':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'mismatch':
        return <AlertTriangle className="h-4 w-4 text-orange-500" />;
      default:
        return null;
    }
  };

  const getStatusClass = () => {
    switch (schemaPropertyParam.status) {
      case 'mapped':
        return 'border-green-200 bg-green-50 hover:bg-green-100 dark:border-green-800 dark:bg-green-900/20 hover:dark:bg-green-900/40';
      case 'missing':
        return 'border-red-200 bg-red-50 hover:bg-red-100 dark:border-red-800 dark:bg-red-900/20  hover:dark:bg-red-900/40';
      case 'incomplete':
        return 'border-red-200 bg-red-50 hover:bg-red-100 dark:border-red-800 dark:bg-red-900/20  hover:dark:bg-red-900/40';
      case 'extra':
        return 'border-yellow-200 bg-yellow-50 hover:bg-yellow-100 dark:border-yellow-800 dark:bg-yellow-900/20  hover:dark:bg-yellow-900/40';
      case 'mismatch':
        return 'border-orange-200 bg-orange-50 hover:bg-orange-100 dark:border-orange-800 dark:bg-orange-900/20  hover:dark:bg-orange-900/40';
      default:
        return '';
    }
  };

  const getTypeIcon = () => {
    if (!param.dataTypes || param.dataTypes.length === 0) return null;
    
    switch (param.dataTypes[0]) {
      case 'string':
        return <Type size={16} />;
      case 'number':
        return <Hash size={16} />;
      case 'boolean':
        return <CheckSquare size={16} />;
      case 'array':
        return <SquareStack size={16} />;
      case 'object':
        return <Box size={16} />;
      case 'null':
        return <CircleDot size={16} />;
      default:
        return <Type size={16} />;
    }
  };

  return (
    <div className="relative">
      <Tooltip>
        <TooltipTrigger>
          <Menu as="div" className="relative inline-block text-left w-full">
            {({ open }) => (
              <>
                <MenuButton
                  className={clsx(
                    "flex items-center min-h-[36px] w-full rounded-lg px-1 my-1 relative",
                    schemaPropertyParam.status && getStatusClass(),
                    "hover:bg-gray-100 dark:hover:bg-gray-700",
                    (schemaPropertyParam.sourceTransformId || shouldShowCreateTransformDropdown()) ? 'cursor-pointer' : 'cursor-default'
                  )}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (onPropertyClick) {
                      onPropertyClick(schemaPropertyParam);
                    }
                  }}
                >
                  <div className="flex items-center gap-x-2 w-full min-w-0">
                    <span className="inline-flex w-6 h-6 justify-center items-center">
                      {getStatusIcon()}
                    </span>

                    {param.dataTypes && param.dataTypes.length > 0 && (
                      <span className={`inline-flex w-6 h-6 justify-center items-center rounded-full ${getTypeColorClass(param.dataTypes[0])}`}>
                        {getTypeIcon()}
                      </span>
                    )}

                    <span 
                      className="font-semibold text-[15px] text-gray-900 dark:text-white break-all leading-tight"
                      title={param.fullPath}
                    >
                      {param.friendlyName || param.fullPath || <span className="italic text-gray-400">Unnamed Parameter</span>}
                    </span>

                    {param.isPii && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900 dark:text-fuchsia-200 whitespace-nowrap">
                        <Shield size={12} className="mr-1" /> PII
                      </span>
                    )}

                    {param.priorities && param.priorities.includes('Required') && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
                        Required
                      </span>
                    )}

                    {/* Show plus icon for properties without transforms */}
                    {shouldShowCreateTransformDropdown() && (
                      <span className="inline-flex w-5 h-5 justify-center items-center ml-auto opacity-0 group-hover:opacity-100 transition-opacity">
                        <Plus className="h-3 w-3 text-gray-400 hover:text-blue-500" />
                      </span>
                    )}
                  </div>
                </MenuButton>

                {shouldShowCreateTransformDropdown() && open && (
                  <MenuItems
                    static
                    className="absolute top-full left-0 z-50 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg py-1 min-w-[200px]"
                  >
                    {getAvailableTransformTypes().map((transformType) => (
                      <MenuItem key={transformType.type}>
                        {({ active }) => (
                          <button
                            onClick={() => handleCreateTransform(transformType.type)}
                            className={clsx(
                              'w-full text-left px-4 py-2 text-sm',
                              active ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'
                            )}
                          >
                            <div className="font-medium">{transformType.label}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">{transformType.description}</div>
                          </button>
                        )}
                      </MenuItem>
                    ))}
                  </MenuItems>
                )}
              </>
            )}
          </Menu>
        </TooltipTrigger>
        <TooltipContent className="max-w-2xl text-sm p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg">
          <div className="space-y-3">
            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                <p className="text-gray-900 dark:text-white font-mono text-xs break-all whitespace-pre-wrap">{param.fullPath}</p>
              </div>
              
              <div className="flex items-start gap-2">
                <Type className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                <p className="text-gray-900 dark:text-white font-medium">{param.dataTypes?.[0] || 'unknown'}</p>
              </div>
            </div>

            {param.description && (
              <>
                <div className="border-t border-gray-200 dark:border-gray-600 pt-2">
                  <div className="flex items-start gap-2">
                    <FileText className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                    <p className="text-gray-900 dark:text-white">{param.description}</p>
                  </div>
                </div>
              </>
            )}

            {param.regexFormula && (
              <>
                <div className="border-t border-gray-200 dark:border-gray-600 pt-2">
                  <div className="flex items-start gap-2">
                    <Regex className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                    <p className="text-gray-900 dark:text-white font-mono text-xs break-all whitespace-pre-wrap">{param.regexFormula}</p>
                  </div>
                </div>
              </>
            )}

            {schemaPropertyParam.source && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  {schemaPropertyParam.sourceAppliesToScope === 'global' ? (
                    <Globe size={16} className="text-gray-500 flex-shrink-0" />
                  ) : schemaPropertyParam.sourceAppliesToScope === 'eventSpecific' ? (
                    <Flag size={16} className="text-gray-500 flex-shrink-0" />
                  ) : null}
                  <p className="text-gray-900 dark:text-white font-medium">{schemaPropertyParam.source}</p>
                </div>

                {(schemaPropertyParam.sourceInputPath || schemaPropertyParam.sourceOutputPath || schemaPropertyParam.sourceTransformType) && (
                  <div className="flex flex-col gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <div className="flex items-center gap-2">
                      {schemaPropertyParam.sourceInputPath ? (
                        <span className="font-mono bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded break-all whitespace-pre-wrap">
                          {schemaPropertyParam.sourceInputPath}
                        </span>
                      ) : (
                        <span className="text-gray-500">No Input</span>
                      )}

                      {schemaPropertyParam.sourceTransformType && (() => {
                        const config = getTransformConfig(schemaPropertyParam.sourceTransformType as PlaybookTransformConfig['type']);
                        const Icon = config?.icon;
                        return Icon ? (
                          <Icon size={16} className={clsx("flex-shrink-0", config?.colorClass.replace('bg-', 'text-'))} />
                        ) : null;
                      })()}

                      {schemaPropertyParam.sourceOutputPath ? (
                        <span className="font-mono bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded break-all whitespace-pre-wrap">
                          {schemaPropertyParam.sourceOutputPath}
                        </span>
                      ) : (
                        <span className="text-gray-500">No Output</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {schemaPropertyParam.status && (
              <div className="border-t border-gray-200 dark:border-gray-600 pt-2">
                <div className="flex items-start gap-2">
                  {getStatusIcon()}
                  <div className="flex-1">
                    <div className={clsx(
                      "px-2 py-1 rounded text-xs font-medium",
                      schemaPropertyParam.status === 'mapped' && !schemaPropertyParam.typeMismatch && "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
                      schemaPropertyParam.status === 'mapped' && schemaPropertyParam.typeMismatch && "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300",
                      schemaPropertyParam.status === 'missing' && "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
                      schemaPropertyParam.status === 'incomplete' && "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
                      schemaPropertyParam.status === 'extra' && "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
                      schemaPropertyParam.status === 'mismatch' && "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300"
                    )}>
                      {schemaPropertyParam.status === 'mapped' ? (
                        schemaPropertyParam.typeMismatch ? 
                          `Type mismatch: Expected ${schemaPropertyParam.typeMismatch.expected}, got ${schemaPropertyParam.typeMismatch.actual}, click to view.` : 
                          'Correctly mapped to target schema, click to view.'
                      ) :
                      schemaPropertyParam.status === 'missing' ? (
                        schemaPropertyParam.sourceTransformId && schemaPropertyParam.sourceEventId && schemaPropertyParam.sourceEventId !== selectedEventId ?
                          'Property not mapped for this event, click to edit.' :
                          'Required by target schema but not generated by any transform, click to add.'
                      ) :
                      schemaPropertyParam.status === 'incomplete' ? 'Has an incomplete transform that needs configuration, click to edit.' :
                      schemaPropertyParam.status === 'extra' ? 'Generated by transforms but not expected in target schema, click to view.' :
                      schemaPropertyParam.status === 'mismatch' ? (
                        schemaPropertyParam.typeMismatch ? 
                          `Type mismatch: Expected ${schemaPropertyParam.typeMismatch.expected}, got ${schemaPropertyParam.typeMismatch.actual}, click to edit.` : 
                          'Type mismatch between derived and target schema, click to edit.'
                      ) : 'Unknown status'}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </div>
  );
};

export default SchemaParameterTreeView;
